import { formatDay, formatLiveClock } from 'pages/data-import/utils';
import { AccountCell, Muted, Sub, TiktokIdCell, UidCell } from '../components/cells';
import IssueTable, { IssueField } from '../components/issue-table';
import { DepartedRow } from '../types';
import { useIssueList } from '../use-issue-list';
import { UNKNOWN_ACCOUNT_TEXT, formatCount, toCount } from '../utils';

// Bề rộng đo bằng font của trang (tiêu đề cột 13px đậm, ô 14px): mỗi tiêu đề và mỗi ngày nằm
// trên một dòng. Tổng 1.095px, vừa khung nội dung của màn 1440 (1.105px khi có thanh cuộn dọc).
const FIELDS: IssueField<DepartedRow>[] = [
  {
    key: 'tiktokUsername',
    title: 'TikTok ID',
    width: 165,
    render: (row) => <TiktokIdCell username={row.tiktokUsername} sub={row.statusText} />,
  },
  {
    key: 'resolvedUid',
    title: 'UID',
    width: 175,
    render: (row) => <UidCell uid={row.resolvedUid} empty="Chưa tìm lại được" />,
  },
  {
    key: 'user',
    title: 'Tài khoản app',
    width: 190,
    // Chưa tìm lại được UID thì không biết tài khoản nào: không ghi "Chưa có tài khoản".
    render: (row) => (
      <AccountCell user={row.user} empty={row.resolvedUid ? undefined : UNKNOWN_ACCOUNT_TEXT} />
    ),
  },
  {
    key: 'firstFlagged',
    title: 'Lần đầu ghi nhận',
    width: 130,
    render: (row) => formatDay(row.firstFlagged),
  },
  {
    key: 'lastFlagged',
    title: 'Lần cuối ghi nhận',
    width: 135,
    render: (row) => (
      <>
        {formatDay(row.lastFlagged)}
        {toCount(row.occurrences) > 1 && <Sub>Ghi nhận {formatCount(row.occurrences)} lần</Sub>}
      </>
    ),
  },
  {
    key: 'lastDiamonds',
    title: 'KC giai đoạn cuối',
    width: 135,
    align: 'right',
    render: (row) => (
      <>
        {formatCount(row.lastDiamonds ?? 0)}
        {toCount(row.lastLiveSeconds) > 0 && (
          <Sub>LIVE {formatLiveClock(toCount(row.lastLiveSeconds))}</Sub>
        )}
      </>
    ),
  },
  {
    key: 'lastActive',
    title: 'Hoạt động lần cuối',
    width: 165,
    render: (row) =>
      row.lastActive ? formatDay(row.lastActive) : <Muted>Chưa thấy hoạt động</Muted>,
  },
];

const rowKey = (row: DepartedRow) => `${row.tiktokUsername}|${row.resolvedUid || ''}`;

/**
 * Tab "Đã rời đại lý": các dòng mà file TikTok thay UID bằng chữ "Nhà sáng tạo đã rời khỏi đại lý".
 * Mỗi TikTok ID một dòng; UID là UID tìm lại được từ các file trước đó (không đoán).
 */
const DepartedTab = () => {
  const list = useIssueList<DepartedRow>('departed', 'Không tải được danh sách Đã rời đại lý');
  return (
    <IssueTable
      list={list}
      fields={FIELDS}
      rowKey={rowKey}
      searchPlaceholder="Tìm TikTok ID, UID hoặc tên"
      searchLabel="Tìm theo TikTok ID, UID hoặc tên trong app"
      note={
        <>
          Creator mà file TikTok ghi là đã rời khỏi đại lý. File không còn UID của họ, nên UID ở
          đây là UID tìm lại được từ các file nhập trước đó. Các dòng này không vào Bảng xếp hạng.
        </>
      }
    />
  );
};

export default DepartedTab;
